import emojiRegex from "emoji-regex";
import { DEVICE_TYPE } from "./enums";
import { StorageHelper } from "./helpers";
import { isMacLike } from "./helpers/a11y/utils";

export class Util {
  // 1. Initialize the regex once as a static readonly property.
  // This executes emojiRegex() only when the module loads.
  private static readonly EMOJI_PATTERN = emojiRegex();
  
    static isNullOrEmpty(value: string) {
        return value == null || value.length === 0;
    }

    static isArray(value) {
        return Array.isArray(value);
    }

    static setCssVariable(key, val) {
        let root = document.documentElement;
        root.style.setProperty(key, val);
    }

    static getDeviceType = function () {
        if (navigator.userAgent.match(/Android/i) ||
            navigator.userAgent.match(/webOS/i) ||
            navigator.userAgent.match(/iPhone/i) ||
            navigator.userAgent.match(/iPod/i) ||
            navigator.userAgent.match(/BlackBerry/i) ||
            navigator.userAgent.match(/Windows Phone/i) ||
            navigator.userAgent.match(/iPad/i)) {
            return DEVICE_TYPE.Mobile;
        } else if (window.innerWidth <= 600) {
            return DEVICE_TYPE.PcLow;
        } else {
            return DEVICE_TYPE.Pc;
        }
    }

    /**
    * Encode all less than & greater than characters in a string
    * @param str The string value that we need to encode
    */
    static htmlEscape(str: string = '') {
        return str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
    * Decode all less than & greater than characters in a string
    * @param str The string value that we need to decode
    */
    static htmlUnEscape(str: string = '') {
        return str.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    }

    /**
    * Remove all script tags from string.
    * @param str The string value that we need to sanitize
    */
    static removeScriptTags(str: string = '') {
        if (!str) return '';
        var regExp = /<script.*?>(.|\n)*?<\/script>/igm
        return str.replace(regExp, '');
    }

    /**
     * A TypeScript implementation of debounce function
     */
    static debounce<F extends (...params: any[]) => void>(func: F, waitMilliseconds = 50)
        : (this: ThisParameterType<F>, ...args: Parameters<F>) => void {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        return function (this: ThisParameterType<F>, ...args: Parameters<F>) {
            const context = this;

            const doLater = function () {
                timeoutId = undefined;
                func.apply(context, args);
            }

            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(doLater, waitMilliseconds);
        }
    }

    /**
     * Throttling is a technique in which, no matter how many times the user fires the event,
     * the attached function will be executed only once in a given time interval.
     * Leading + trailing: fires immediately on the first call, then — if further calls arrive
     * before `wait` elapses — fires once more at the end of the window with the latest args,
     * so a burst of calls (e.g. several resize events from a rapid zoom change) doesn't leave
     * the caller acting on a stale, intermediate call while the final one is silently dropped.
     * @param {Function} fn This is the function we need to Throttle
     * @param {number} wait This is the time in milliseconds that we need to wait
     * @example let throttledFunc = throttle(myFunc, 500);
     */
    static throttle(fn: Function, wait: number = 50) {
        let isCalled = false;
        let pendingArgs: any[] | null = null;

        const invoke = (args: any[]) => {
            fn(...args);
            isCalled = true;
            setTimeout(() => {
                isCalled = false;
                if (pendingArgs) {
                    const latestArgs = pendingArgs;
                    pendingArgs = null;
                    invoke(latestArgs);
                }
            }, wait);
        };

        return function (...args) {
            if (!isCalled) {
                invoke(args);
            } else {
                pendingArgs = args;
            }
        };
    }

    /**
     * The function `isValidJSON` checks if a given string is a valid JSON.
     * @param {string} str - The `str` parameter is a string that represents a JSON object.
     * @returns The function `isValidJSON` returns a boolean value. It returns `true` if the input string
     * `str` is a valid JSON, and `false` otherwise.
     */
    static isValidJSON(str: string = '') {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    /**
     * This function validates and parses a JSON payload, returning the parsed
     * object if successful or null if there is an error.
     * @param payload - The payload parameter is expected to be a string containing JSON data.
     * @returns the parsed JSON payload if it is valid, otherwise it returns null.
     */
    static validateAndParsePayload(payload) {
        try {
            if (!payload || typeof payload !== 'string') {
                throw new Error('Payload is null or not a string');
            }

            if (!this.isValidJSON(payload)) {
                throw new Error('Payload is not valid JSON');
            }

            return JSON.parse(payload);
        } catch (ex) {
            return null;
        }
    }

    /**
     * Function to check if a variable is an object,
     * and if it has a property that is an array with length > 0
     */
    static isObjectWithNonEmptyArray(obj, propertyName) {
        try {
            return (
                typeof obj === 'object' &&
                obj !== null &&
                propertyName in obj &&
                Array.isArray(obj[propertyName]) &&
                obj[propertyName].length > 0
            );
        } catch (ex) {
            return false;
        }
    }

    /**
     * The function takes a string representing a date and returns a Date object,
     * or the current date if the input is invalid.
     * @param {string} dateString - The dateString parameter is a string that represents a date.
     * @returns a Date object.
     */
    static convertStringToDate(dateString: string) {
        let date = null;
        try {
            if (!dateString) {
                throw new Error("Input date string is empty or null.");
            }
            const dateObject = new Date(dateString);
            if (isNaN(dateObject.getTime())) {
                throw new Error("Invalid date representation.");
            }
            date = dateObject;
        } catch (ex) {
            date = null;
        }
        return date;
    }

    /**
     * The function checks if a terms consent has been accepted and is still valid based on the
     * expiration time.
     * @param pageId - The pageId parameter is a unique identifier for a specific page or section of a
     * website. It is used to retrieve the consent data from local storage.
     * @param expirationInSecs - The expirationInSecs parameter is the duration in seconds for which
     * the consent is considered valid.
     * @returns a boolean value. It returns true if the terms consent is still valid and has not
     * expired, and it returns false if there is no consent data in local storage, if the date in local
     * storage is invalid, or if the consent has expired.
     */
    static isTermsConsentAccepted(pageId, expirationInSecs = "2592000") {
        try {
            let data = StorageHelper.get(`swc_cnst_dt_${pageId}`) || "";
            if (!data) return false; // No consent data in local storage

            let consentExpDate = this.convertStringToDate(window.atob(data)) || "";
            if (!consentExpDate) return false; // Invalid date in local storage

            // Get the expiration time in seconds
            // let timeInSeconds = Number(expirationInSecs);
            // if (isNaN(timeInSeconds)) {
            //     //console.error('Invalid seconds value provided. Using default value.');
            //     timeInSeconds = 2592000; // Default value: 30 days in seconds
            // }

            const currentTime = new Date().getTime();
            const expirationTime = new Date(consentExpDate).getTime();
            //const expirationInMillis = timeInSeconds * 1000; // in milliseconds

            if (expirationTime < currentTime) {
                return false; // Consent has expired
            }

            return true; // Consent is still valid
        } catch (ex) {
            return false;
        }
    }

    /**
     * The function checks if a given object has a non-null and non-empty value for a specified
     * property.
     * @param obj - The `obj` parameter is the object that you want to check if it has a specific
     * property with a value.
     * @param propertyName - The propertyName parameter is a string that represents the name of the
     * property that we want to check for a value in the obj object.
     * @returns a boolean value. It returns true if the object has a non-null, non-undefined, and
     * non-empty value for the specified property name. Otherwise, it returns false.
     */
    static propertyHasValue(obj, propertyName) {
        try {
            return (
                typeof obj === 'object' &&
                obj !== null &&
                propertyName in obj &&
                obj[propertyName] !== null &&
                obj[propertyName] !== undefined &&
                obj[propertyName] !== ''
            );
        } catch (ex) {
            return false;
        }
    }

    /**
     * The function takes an optional parameter `seconds` and returns a new Date
     * object that is the current date plus the specified number of seconds.
     * @param [seconds=2592000] - The "seconds" parameter is a string representing the number of
     * seconds to add to the current date. The default value is "2592000", which corresponds to 30
     * days.
     * @returns a new Date object that represents the current time plus the specified number of
     * seconds. If an error occurs, it will return the current date and time.
     */
    static addSecondsToDate(seconds = "2592000") {
        try {
            let timeInSeconds = Number(seconds);
            if (isNaN(timeInSeconds)) {
                //console.error('Invalid seconds value provided. Using default value.');
                timeInSeconds = 2592000; // Default value: 30 days in seconds
            }

            const currentTime = new Date().getTime();
            const expirationInMillis = timeInSeconds * 1000; // in milliseconds
            return new Date(currentTime + expirationInMillis);
        } catch (error) {
            return new Date();
        }
    }

    /**
     * Retrieves the font style properties based on the given components and type.
     *
     * @param components - The array of components.
     * @param type - The type of the component to search for.
     * @returns The font style properties as an object.
     */
    static getFontStyle(components: any, type: any) {
      let fontStyle: any = {};
      try {
        if (!components) return fontStyle;
        if (!Array.isArray(components)) return fontStyle;
        if (components.length === 0) return fontStyle;

        // Check if an item exists in the array with given type
        const headerText = components.find((item) => item.type === type);
        if (!headerText) return fontStyle;

        // Check if the item has a "font" property
        if (!headerText.font) return fontStyle;

        // Check if it is an object and not empty
        if (typeof headerText.font !== "object") return fontStyle;
        if (Object.keys(headerText.font).length === 0) return fontStyle;

        // Parse the font properties
        const font = headerText.font || {};

        // Set the font style properties
        if (font.size !== undefined && !this.isNullOrEmpty(font.size)) {
          fontStyle.fontSize = font.size;
        }
        if (font.bold !== undefined) {
          fontStyle.fontWeight = font.bold ? "bold" : "normal";
        }
        if (font.italic !== undefined) {
          fontStyle.fontStyle = font.italic ? "italic" : "normal";
        }
        if (font.color !== undefined && !this.isNullOrEmpty(font.color)) {
          fontStyle.color = font.color;
        }
        if (font.alignment !== undefined && !this.isNullOrEmpty(font.alignment)) {
          fontStyle.textAlign = font.alignment;
        }
      } catch (ex) {
        fontStyle = {};
      }
      return fontStyle;
    }

    /**
     * Retrieves the font style for a given component and type.
     *
     * @param component - The component object to retrieve the font style from.
     * @param type - The type of the component to match against.
     * @returns The font style object with properties such as fontSize, fontWeight, fontStyle, color, and textAlign.
     */
    static getLabelFontStyle(component: any, type: any) {
      let fontStyle: any = {};
      try {
        if (!component) return fontStyle;

        // Check component is object and not empty
        if (typeof component !== "object" || Object.keys(component).length === 0) return fontStyle;

        // Check if in the "component" we have a "type" property and it is equal to the given type
        if (!component.type || component.type !== type) return fontStyle;

        // The component is a valid object with the given type
        const headerText = component;
        if (!headerText) return fontStyle;

        // Check if the item has a "font" property
        if (!headerText.font) return fontStyle;

        // Check if it is an object and not empty
        if (typeof headerText.font !== "object") return fontStyle;
        if (Object.keys(headerText.font).length === 0) return fontStyle;

        // Parse the font properties
        const font = headerText.font || {};

        // Set the font style properties
        if (font.size !== undefined && !this.isNullOrEmpty(font.size)) {
          fontStyle.fontSize = font.size;
        }
        if (font.bold !== undefined) {
          fontStyle.fontWeight = font.bold ? "bold" : "normal";
        }
        if (font.italic !== undefined) {
          fontStyle.fontStyle = font.italic ? "italic" : "normal";
        }
        if (font.color !== undefined && !this.isNullOrEmpty(font.color)) {
          fontStyle.color = font.color;
        }
        if (font.alignment !== undefined && !this.isNullOrEmpty(font.alignment)) {
          fontStyle.textAlign = font.alignment;
        }
      } catch (ex) {
        fontStyle = {};
      }
      return fontStyle;
    }

    /**
     * Sanitizes a string input by removing HTML and script tags for security purposes.
     *
     * @param input - The string to sanitize. Defaults to empty string if not provided.
     * @returns A sanitized string with all HTML and script tags removed.
     *
     * @example
     * ```ts
     * const dirty = "<script>alert('xss')</script><div>Hello</div>";
     * const clean = Util.sanitizeInput(dirty); // Returns: "Hello"
     * ```
     */
    static sanitizeInput(input: string = ''): string {
      if (!input || typeof input !== 'string') return '';

      // Remove <script> tags and their content
      const removeScriptTags = input.replace(/<script.*?>.*?<\/script>/gi, '');

      // Remove all other HTML tags (e.g., <div>, <p>, etc.)
      const removeHtmlTags = removeScriptTags.replace(/<\/?[^>]+(>|$)/g, '');
      return removeHtmlTags;
    }

    /**
     * Trims whitespace from the beginning and end of a string input.
     *
     * @param input - The string to trim. If input is not a string, it will be converted to a string.
     * @returns The trimmed string with whitespace removed from the beginning and end.
     */
    static trimString(input?: unknown): string {
      try {
        if (input == null) return "";
        // Ensure input is a string before calling string methods.
        if (typeof input === "string") {
          return input.trim();
        } else {
          // If input is not a string, attempt to convert to string.
          return String(input).trim();
        }
      } catch (ex) {
        // Return an empty string to signify an error in trimming.
        return "";
      }
    }

    /**
     * Validates if the given string is a properly formatted URL.
     *
     * This method tries to create a URL object from the provided string.
     * If successful, it indicates that the string is a valid URL.
     *
     * @param url - The string to validate as URL
     * @returns `true` if the string is a valid URL, `false` otherwise
     */
    static isValidUrl(url: string): boolean {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    }

    /**
     * Decodes a URI component using `decodeURIComponent`.
     * If the decoding fails (e.g., due to malformed URI), returns the original URI.
     *
     * @param uri - The URI component to decode
     * @returns The decoded URI if successful, or the original URI if decoding fails
     */
    static decodeURI(uri: string): string {
      try {
        return decodeURIComponent(uri);
      } catch (e: unknown) {
        return uri;
      }
    }

    /**
     * Regular expression to detect invalid URL protocols.
     * This pattern matches protocols such as 'javascript:', 'data:', and 'vbscript:'.
     */
    private static invalidProtocolRegex = /^(javascript|data|vbscript):/i;

    /**
     * Sanitizes a URL by performing various validation and normalization steps.
     *
     * The function:
     * - Trims whitespace and decodes any URI components
     * - Rejects URLs with invalid protocols
     * - Adds 'http://' prefix if no protocol is specified
     * - Validates the URL format
     * - Ensures the protocol is either HTTP or HTTPS
     * - Normalizes the protocol and hostname to lowercase
     *
     * @param url - The URL string to sanitize
     * @returns A sanitized URL string, or an empty string if the URL is invalid
     */
    static sanitizeUrl(url?: string): string {
      try {
        if (!url) return "";
        let trimmedUrl = Util.decodeURI(url.trim());

        // Check if the URL starts with an invalid protocol
        if (Util.invalidProtocolRegex.test(trimmedUrl)) return "";

        // If the URL doesn't start with a protocol, assume 'http://'
        if (!/^https?:\/\//i.test(trimmedUrl)) {
          trimmedUrl = `http://${trimmedUrl}`;
        }

        if (!Util.isValidUrl(trimmedUrl)) {
          console.warn(`Invalid URL: ${trimmedUrl}`);
          return "";
        }

        const urlObj = new URL(trimmedUrl);
        const validProtocols = ["http:", "https:"];
        if (!validProtocols.includes(urlObj.protocol)) {
          console.warn(`Unsupported protocol: ${urlObj.protocol}`);
          return "";
        }

        // Normalize protocol and hostname to lowercase
        urlObj.protocol = urlObj.protocol.toLowerCase();
        urlObj.hostname = urlObj.hostname.toLowerCase();

        return urlObj.toString();
      } catch (ex) {
        //console.error('Error sanitizing URL:', ex);
        return "";
      }
    }

		/**
		 * Validates if a timestamp is a valid future cooldown expiry.
		 * @param expiry - The timestamp to validate
		 * @param maxHoursAhead - Maximum hours in the future (default: 24)
		 * @returns {boolean} True if valid
		 */
		static isValidCooldownExpiry(expiry: any, maxHoursAhead: number = 24): boolean {
			try {
				// Must be a number
				if (typeof expiry !== "number") return false;

				// Must be finite
				if (!Number.isFinite(expiry)) return false;

				const now = Date.now();
				const maxFuture = now + maxHoursAhead * 60 * 60 * 1000; // Convert hours to ms

				// Must be in the future
				if (expiry <= now) return false;

				// Must not be too far in the future
				if (expiry > maxFuture) return false;
				return true;
			} catch (ex) {
				//console.error("Error validating cooldown expiry:", ex);
				return false;
			}
		}

		/**
		 * Determines if feedback for a specified message is currently in a cooldown period.
		 *
		 * @param state - The state object containing feedback cooldown information.
		 * @param messageId - The identifier of the message to check for cooldown.
		 * @returns {boolean} - Returns true if the message is still within its cooldown period,
		 *                      false if the cooldown has expired or if there's any error during the check.
		 */
		static isFeedbackInCooldown(state: any, messageId: string): boolean {
			try {
				if (!state || !state.feedbackCooldowns || !messageId) return false;
				const entry = state.feedbackCooldowns[messageId];
				if (!entry || !Util.isValidCooldownExpiry(entry.expiry)) return false;
				return Date.now() < entry.expiry; // true if still in cooldown window
			} catch (ex) {
				//console.error("Error checking feedback cooldown:", ex);
				return false;
			}
		}

		/**
		 * Determines if a value is a plain object (not null, not an array).
		 * Keeps checks more explicit and testable.
		 */
		static isPlainObject(value: unknown): value is Record<string, unknown> {
			try {
				return !!value && typeof value === "object" && !Array.isArray(value);
			} catch (ex) {
				//console.error("Error checking if plain object:", ex);
				return false;
			}
		}
    
    /**
     * SD-11137
     * Formats text by wrapping emojis in a span with aria-hidden="true".
     * Uses a pre-compiled regex pattern for performance.
     */
    static formatTextWithAccessibleEmojis(text: string) {
      if (!text) return '';
      try {
    
        // Wrap each emoji in an aria-hidden span
        return text.replace(this.EMOJI_PATTERN, (emoji) => {
          /**
           * Especially while rendering flags, the font color is changing in Safari browser.
           * This is a known issue with Safari and Apple Color Emoji font. 
           * By explicitly setting the font-family to 'Apple Color Emoji'.
           */ 
           
          const emojiStyle = isMacLike() ? 'style="font-family: \'Apple Color Emoji\', sans-serif"' : '';
          return `<span aria-hidden="true" ${emojiStyle}>${emoji}</span>`;
        });
      } catch (err) {
        // If something thing went wrong return the text as it is
        return text
      }
    }
    
    /**
     * Debounces scroll events on a given element, resolving a promise when scrolling has stopped 
     * for a specified idle time or after a maximum time limit.
     */
    static waitForScrollStop(el: HTMLElement, idleMs = 120, maxMs = 1800): Promise<void> {
      try {
        return new Promise((resolve) => {
          let idleTimer: number | undefined;
          let maxTimer: number | undefined;

          const done = () => {
            try {
              el.removeEventListener("scroll", onScroll);
              if (idleTimer != null) window.clearTimeout(idleTimer);
              if (maxTimer != null) window.clearTimeout(maxTimer);
            } catch {
              // no-op
            } finally {
              resolve();
            }
          }

          const onScroll = () => {
            try {
              if (idleTimer != null) window.clearTimeout(idleTimer);
              idleTimer = window.setTimeout(done, idleMs);
            } catch {
              done();
            }
          }

          try {
            el.addEventListener("scroll", onScroll, { passive: true });
            maxTimer = window.setTimeout(done, maxMs);
            idleTimer = window.setTimeout(done, idleMs);
          } catch {
            done();
          }
        })
      } catch {
        return Promise.resolve();
      }
    }
    
    /**
     * Smoothly scrolls an element to a desired top position and verifies if the scroll was successful.
     */
    static async smoothScrollAndVerify(element: HTMLElement, desiredTop: number): Promise<boolean> {
      try {
        const maxTop = Math.max(0, element.scrollHeight - element.clientHeight);
        const target = Math.min(Math.max(0, desiredTop), maxTop);

        try {
          element.scroll({ top: target, behavior: "smooth" });
        } catch {
          element.scrollTop = target;
          return true;
        }

        await this.waitForScrollStop(element);
        const reached = Math.abs(element.scrollTop - target) <= 2;
        if (!reached) element.scrollTop = target;
        return reached;
      } catch {
        return false;
      }
    }
    
    /**
     * Queue after layout using requestAnimationFrame to ensure the callback runs after the next paint, 
     * which is useful for smooth scrolling on certain platforms.
     */
    static nextFrame(): Promise<void> {
      return new Promise((resolve) => {
        try {
          requestAnimationFrame(() => resolve());
        } catch {
          resolve();
        }
      });
    }
}
